/**
 * MessengerLinks — quick-launch buttons for popular messengers tied
 * to a phone number. Used in OrderDetailDrawer (customer phone shown
 * to admin/operator) and in CustomerProfileDrawer (admin profile
 * view) so admin can ping the customer in one click.
 *
 * We can't truly detect availability without contacting WhatsApp /
 * Telegram servers, but for any normalised phone number we offer
 * `wa.me` / `t.me` / `viber://` deep-links. They auto-fail-open if
 * the user doesn't have the target app installed.
 *
 * Props:
 *   phone   — string. Anything; non-digits are stripped.
 *   compact — when true, hides the textual labels next to the icons
 *             (icon-only). Default false.
 */
import React from 'react';
import { MessageSquare } from 'lucide-react';

export default function MessengerLinks({ phone, compact = false }) {
  if (!phone) return null;
  const digits = String(phone).replace(/[^0-9]/g, '');
  if (!digits) return null;

  const links = [
    {
      id: 'whatsapp',
      label: 'WhatsApp',
      cls: 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200',
      // `whatsapp://send?phone=` opens the native WhatsApp app directly
      // (Desktop or mobile) — bypasses `wa.me`/`api.whatsapp.com` redirect
      // chain that's blocked by Roskomnadzor and a few other carriers.
      // If the app isn't installed the browser raises "protocol not
      // handled" and nothing happens — no censorship hit either way.
      href: `whatsapp://send?phone=${digits}`,
      svg: (
        <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="currentColor">
          <path d="M17.5 14.4c-.3-.2-1.7-.8-2-.9-.3-.1-.5-.2-.7.2-.2.3-.8.9-.9 1.1-.2.2-.3.2-.6.1-.3-.1-1.2-.5-2.4-1.5-.9-.8-1.5-1.7-1.6-2-.2-.3 0-.5.1-.6.1-.1.3-.3.4-.5.1-.2.2-.3.3-.5.1-.2 0-.4 0-.5-.1-.1-.7-1.6-.9-2.2-.2-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.7.4-.3.3-1 1-1 2.4 0 1.4 1 2.8 1.2 3 .1.2 2.1 3.2 5.1 4.5.7.3 1.3.5 1.7.6.7.2 1.4.2 1.9.1.6-.1 1.7-.7 2-1.4.2-.7.2-1.2.2-1.4-.1-.1-.3-.2-.6-.4zM12 22a10 10 0 0 1-5.1-1.4L2 22l1.4-4.8A10 10 0 1 1 12 22z" />
        </svg>
      ),
    },
    {
      id: 'telegram',
      label: 'Telegram',
      cls: 'bg-sky-100 text-sky-700 hover:bg-sky-200',
      href: `https://t.me/+${digits}`,
      svg: (
        <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="currentColor">
          <path d="M22 2 11 13M22 2l-7 20-4-9-9-4 20-7z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" fill="none" />
        </svg>
      ),
    },
    {
      id: 'viber',
      label: 'Viber',
      cls: 'bg-violet-100 text-violet-700 hover:bg-violet-200',
      href: `viber://chat?number=%2B${digits}`,
      svg: (
        <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="currentColor">
          <path d="M12 2C6.5 2 2 6.5 2 12c0 1.7.4 3.3 1.2 4.7L2 22l5.5-1.2c1.4.7 3 1.2 4.5 1.2 5.5 0 10-4.5 10-10S17.5 2 12 2zm5.7 13.4c-.3.7-1.5 1.3-2.1 1.4-.6.1-1.3.1-2-.1-.4-.1-1-.3-1.7-.6-3-1.3-5-4.3-5.1-4.5-.2-.2-1.2-1.6-1.2-3 0-1.4.7-2.1 1-2.4.3-.3.6-.4.8-.4h.5c.2 0 .4 0 .6.4.2.5.7 1.7.8 1.8.1.2.2.3 0 .5-.1.2-.2.3-.4.5-.1.2-.3.4-.4.5-.1.2-.3.3-.1.6.2.3.8 1.3 1.7 2.1 1.1.9 2 1.2 2.4 1.4.3.2.5.1.7-.1.2-.2.7-.8.9-1.1.2-.3.4-.2.7-.1.3.1 1.7.8 2 .9.3.2.5.2.5.4.1.1.1.7-.1 1.4z" />
        </svg>
      ),
    },
    {
      id: 'sms',
      label: 'SMS',
      cls: 'bg-slate-100 text-slate-700 hover:bg-slate-200',
      href: `sms:${phone}`,
      svg: <MessageSquare className="w-3.5 h-3.5" />,
    },
  ];

  return (
    <div className="inline-flex items-center gap-1.5 flex-wrap" data-testid="messenger-links">
      {links.map((l) => (
        <a
          key={l.id}
          href={l.href}
          target="_blank"
          rel="noopener noreferrer"
          className={`inline-flex items-center gap-1 ${compact ? 'p-1.5' : 'px-2 py-1'} rounded-md text-[10px] font-bold transition ${l.cls}`}
          title={`Open ${l.label}`}
          data-testid={`msgr-${l.id}`}
          // Stop click bubbling — when the chip lives inside a
          // clickable card/row the messenger-tap should NOT navigate
          // the parent.
          onClick={(e) => e.stopPropagation()}
        >
          {l.svg}
          {!compact && <span>{l.label}</span>}
        </a>
      ))}
    </div>
  );
}
